import { LTV_THRESHOLD, OptionTypeEnum } from '../gql/financialInfoGql';
import { ProductSlug } from '../gql/generated/graphql';

import { getLtvRatio } from '../utils/deals';

export const getProductShortLabel = (slug: ProductSlug): string => {
  const PRODUCT_SHORT_LABELS: Partial<Record<ProductSlug, string>> = {
    [ProductSlug.Vsc]: 'VSC',
    [ProductSlug.Gap]: 'GAP',
    [ProductSlug.TireWheel]: 'T&W',
    [ProductSlug.KeyReplacement]: 'Key',
  };
  return PRODUCT_SHORT_LABELS[slug] ?? slug;
};

export type ProductAvailability = {
  available: boolean;
  reason?: string;
};

type GapAvailabilityContext = {
  amountFinanced?: number | null;
  retailBookValue?: number | null;
  missingInfo: boolean;
};

export type PackageConfig = {
  id: string;
  slotIndex: number;
  selectedProductSlugs: ProductSlug[];
  displayName?: string;
};

export type PersistedPackageConfig = Pick<PackageConfig, 'slotIndex' | 'selectedProductSlugs'>;

export type PackagePaymentResult = {
  packageId: string;
  amountFinanced: number;
  buyPayment: number;
  sellPayment: number;
  profit: number;
  setterCommission: number;
  closerCommission: number;
  reserve: number;
  products: Array<{
    slug: ProductSlug;
    price: number;
    cost: number;
  }>;
};

export const DEFAULT_PACKAGE_CONFIGS: PackageConfig[] = [
  {
    id: 'package1',
    slotIndex: 1,
    selectedProductSlugs: [ProductSlug.Vsc, ProductSlug.Gap],
    displayName: 'VSC + GAP',
  },
  {
    id: 'package2',
    slotIndex: 2,
    selectedProductSlugs: [ProductSlug.Vsc],
    displayName: 'VSC Only',
  },
  {
    id: 'package3',
    slotIndex: 3,
    selectedProductSlugs: [ProductSlug.Gap],
    displayName: 'GAP Only',
  },
  {
    id: 'package4',
    slotIndex: 4,
    selectedProductSlugs: [],
    displayName: 'No Protection',
  },
];

export const normalizeProductSlugs = (selectedSlugs: ProductSlug[]): ProductSlug[] => {
  return Array.from(new Set(selectedSlugs)).sort((a, b) => a.localeCompare(b));
};

export const getProductSignature = (selectedSlugs: ProductSlug[]): string => {
  const normalizedSlugs = normalizeProductSlugs(selectedSlugs);
  if (normalizedSlugs.length === 0) {
    return 'none';
  }

  return normalizedSlugs.join('|');
};

export const generatePackageDisplayName = (
  selectedSlugs: ProductSlug[],
  products: Array<{ slug: ProductSlug; name: string }>,
): string => {
  if (selectedSlugs.length === 0) {
    return 'No Protection';
  }

  const orderedProductSlugs = products
    .filter((product) => selectedSlugs.includes(product.slug))
    .map((product) => product.slug);
  const slugsToDisplay = orderedProductSlugs.length > 0 ? orderedProductSlugs : selectedSlugs;

  if (slugsToDisplay.length === 1) {
    const shortLabel = getProductShortLabel(slugsToDisplay[0]);
    return `${shortLabel} Only`;
  }

  return slugsToDisplay.map((slug) => getProductShortLabel(slug)).join(' + ');
};

export const getGapProductAvailability = ({
  amountFinanced,
  retailBookValue,
  missingInfo,
}: GapAvailabilityContext): ProductAvailability => {
  if (missingInfo || !retailBookValue) {
    return { available: false, reason: 'Missing required information' };
  }

  if (!amountFinanced || getLtvRatio(amountFinanced, retailBookValue) < LTV_THRESHOLD) {
    return { available: false, reason: `LTV must be at least ${LTV_THRESHOLD * 100}%` };
  }

  return { available: true };
};

export const findFirstAvailableNonGapPackage = <
  T extends Pick<PackageConfig, 'slotIndex' | 'selectedProductSlugs'>,
>(
  configs: T[],
  isPackageAvailable: (config: T) => boolean,
): T | undefined =>
  [...configs]
    .sort((left, right) => left.slotIndex - right.slotIndex)
    .find(
      (config) =>
        !config.selectedProductSlugs.includes(ProductSlug.Gap) && isPackageAvailable(config),
    );

export const findSelectedProductSlugsForSlotIndex = <
  T extends Pick<PackageConfig, 'slotIndex' | 'selectedProductSlugs'>,
>(
  configs: T[],
  selectedSlotIndex?: number | null,
): ProductSlug[] | null => {
  if (selectedSlotIndex == null) {
    return null;
  }

  const config = configs.find((item) => item.slotIndex === selectedSlotIndex);
  return config ? normalizeProductSlugs(config.selectedProductSlugs ?? []) : null;
};

export const getOptionTypeFromSlugs = (slugs: ProductSlug[]): OptionTypeEnum => {
  const hasVsc = slugs.includes(ProductSlug.Vsc);
  const hasGap = slugs.includes(ProductSlug.Gap);

  if (hasVsc && hasGap) {
    return OptionTypeEnum.VscPlusGap;
  }
  if (hasVsc) {
    return OptionTypeEnum.VSC;
  }
  if (hasGap) {
    return OptionTypeEnum.Gap;
  }
  return OptionTypeEnum.NoProducts;
};

export const getSelectedOptionTypeForSelection = ({
  selectedPackageSlotIndex,
  packageConfigs,
  optionType,
}: {
  selectedPackageSlotIndex?: number | null;
  packageConfigs?: PersistedPackageConfig[] | PackageConfig[];
  optionType?: OptionTypeEnum | null;
}): OptionTypeEnum | null => {
  const effectiveConfigs =
    packageConfigs && packageConfigs.length > 0 ? packageConfigs : DEFAULT_PACKAGE_CONFIGS;
  const selectedSlugs = findSelectedProductSlugsForSlotIndex(
    effectiveConfigs,
    selectedPackageSlotIndex,
  );

  if (selectedSlugs != null) {
    return getOptionTypeFromSlugs(selectedSlugs);
  }

  return optionType ?? null;
};
